import React from "react";
import { Button, LinearProgress } from "@mui/material";
import "./Stepper.component.sass";
import { useTranslation } from "react-i18next";

export interface StepperComponentProps {
  steps?: number;
  activeStep?: number;
  onChangeActiveStep: (stepIndex: number) => void;
  onFinish: () => void;

  isLoading: boolean;
  nextStepDisabled: boolean;
}

export function StepperComponent({
  steps,
  activeStep,
  onChangeActiveStep,
  onFinish,
  isLoading,
  nextStepDisabled,
}: StepperComponentProps) {
  const { t: scanTranslations } = useTranslation("scan-page", {
    keyPrefix: "stepper",
    useSuspense: false,
  });

  const isActiveCategorySet = () => activeStep !== undefined;

  const isLastStep = () =>
    isActiveCategorySet() && !!steps ? activeStep! + 1 === steps : false;

  const normalise = (value: number) =>
    isActiveCategorySet() && !!steps ? (value * 100) / steps : 0;

  const previousCategory = () => {
    if (!!activeStep) {
      onChangeActiveStep(activeStep - 1);
    }
  };

  const nextCategory = () => {
    if (activeStep === undefined) {
      // Initialize active category and start scan
      onChangeActiveStep(0);
    } else if (isLastStep()) {
      // Finish the scan
      onFinish();
    } else {
      // Set next category as active
      onChangeActiveStep(activeStep + 1);
    }
  };

  const renderPreviousStepButton = () => {
    if (!isActiveCategorySet()) {
      return <span className="spacer" />;
    }

    return (
      <Button
        disabled={activeStep! === 0 || isLoading}
        variant="contained"
        color="primary"
        onClick={() => previousCategory()}
      >
        {scanTranslations("previous")}
      </Button>
    );
  };

  const renderNextStepButton = () => {
    let text = scanTranslations("next");

    if (isActiveCategorySet()) {
      if (isLastStep()) {
        text = scanTranslations("finish");
      } else {
        text = scanTranslations("next");
      }
    }

    return (
      <Button
        disabled={nextStepDisabled || isLoading}
        variant="contained"
        color="secondary"
        onClick={() => nextCategory()}
      >
        {text}
      </Button>
    );
  };

  return (
    <div className="stepper">
      <div className="stepper__inner">
        {renderPreviousStepButton()}
        {isActiveCategorySet() && (
          <div className="stepper__inner__progress">
            <span className="percentage">
              {scanTranslations("progress", {
                percentage: Math.ceil(normalise(activeStep!)),
              })}
            </span>
            <LinearProgress
              className="stepper__inner__progress__bar"
              value={normalise(activeStep!)}
              variant="determinate"
            />
          </div>
        )}
        {renderNextStepButton()}
      </div>
    </div>
  );
}
